import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { isOwner } from '../lib/roles';
import { AppNavbar } from '../components/AppNavbar';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, Users, DollarSign, TrendingUp, X, Loader2, Shield, Crown, User as UserIcon, Ban, Stethoscope, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_COLORS = {
  starter: 'bg-gray-100 text-gray-700',
  pro: 'bg-violet-100 text-violet-700',
  team: 'bg-emerald-100 text-emerald-700',
};

const ROLE_BADGES = {
  user: { icon: UserIcon, label: 'User', cls: 'bg-gray-100 text-gray-600' },
  admin: { icon: Shield, label: 'Admin', cls: 'bg-orange-100 text-orange-700' },
  owner: { icon: Crown, label: 'Owner', cls: 'bg-yellow-100 text-yellow-800' },
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-wider text-gray-500" style={{ fontFamily: 'Manrope, sans-serif' }}>{label}</span>
      <Icon className={`h-4 w-4 ${accent || 'text-gray-400'}`} />
    </div>
    <div className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</div>
  </div>
);

const RoleBadge = ({ role }) => {
  const cfg = ROLE_BADGES[role] || ROLE_BADGES.user;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </span>
  );
};

const UserDrawer = ({ targetUser, onClose, onSaved }) => {
  const [planType, setPlanType] = useState(targetUser?.plan_type || 'starter');
  const [role, setRole] = useState(targetUser?.role || 'user');
  const [suspended, setSuspended] = useState(Boolean(targetUser?.suspended));
  const [saving, setSaving] = useState(false);

  if (!targetUser) return null;

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.put(
        `${API}/admin/users/${targetUser.user_id}`,
        { plan_type: planType, role, suspended },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Updated ${targetUser.email}`);
      onSaved(data.user);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${targetUser.email}? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${targetUser.user_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Deleted ${targetUser.email}`);
      onSaved(null, targetUser.user_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="user-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-white shadow-xl overflow-y-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <div className="p-6 border-b border-gray-200 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="drawer-user-name">
              {targetUser.name}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5" data-testid="drawer-user-email">{targetUser.email}</p>
            <p className="text-xs text-gray-400 mt-1 font-mono">{targetUser.user_id}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" data-testid="drawer-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500 block mb-2">Plan</label>
            <div className="grid grid-cols-3 gap-2">
              {['starter', 'pro', 'team'].map(p => (
                <button
                  key={p}
                  onClick={() => setPlanType(p)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${planType === p ? 'bg-black text-white border-black' : 'bg-white border-gray-200 hover:border-gray-300'}`}
                  data-testid={`plan-${p}`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500 block mb-2">Role</label>
            <div className="grid grid-cols-3 gap-2">
              {['user', 'admin', 'owner'].map(r => (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all ${role === r ? 'bg-black text-white border-black' : 'bg-white border-gray-200 hover:border-gray-300'}`}
                  data-testid={`role-${r}`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 p-4 flex items-start gap-3">
            <Ban className={`h-5 w-5 mt-0.5 flex-shrink-0 ${suspended ? 'text-red-600' : 'text-gray-400'}`} />
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3">
                <div className="font-medium text-sm">Suspend account</div>
                <button
                  onClick={() => setSuspended(!suspended)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${suspended ? 'bg-red-600' : 'bg-gray-200'}`}
                  data-testid="suspend-toggle"
                  aria-pressed={suspended}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${suspended ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {suspended ? 'User cannot sign in. Their data is preserved.' : 'User has normal access.'}
              </p>
            </div>
          </div>

          <div className="text-xs text-gray-500 space-y-1 pt-2 border-t border-gray-100">
            <div>Joined: {new Date(targetUser.created_at).toLocaleString()}</div>
            {targetUser.subscription_updated_at && (
              <div>Subscription updated: {new Date(targetUser.subscription_updated_at).toLocaleString()}</div>
            )}
            <div>Org ID: <span className="font-mono">{targetUser.org_id || '—'}</span></div>
          </div>

          <div className="flex items-center justify-between gap-3 pt-4 border-t border-gray-100">
            <Button variant="ghost" onClick={remove} disabled={saving} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="drawer-delete-btn">
              Delete user
            </Button>
            <Button onClick={save} disabled={saving} className="bg-black hover:bg-black/90" data-testid="drawer-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Save changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const STATUS_STYLES = {
  pass: { icon: CheckCircle2, cls: 'text-emerald-600', badge: 'bg-emerald-100 text-emerald-700', label: 'Healthy' },
  warn: { icon: AlertTriangle, cls: 'text-amber-600', badge: 'bg-amber-100 text-amber-700', label: 'Missing fields' },
  fail: { icon: XCircle, cls: 'text-red-600', badge: 'bg-red-100 text-red-700', label: 'Broken' },
  error: { icon: XCircle, cls: 'text-red-600', badge: 'bg-red-100 text-red-700', label: 'Error' },
};

const ScraperSelfTest = () => {
  const [url, setUrl] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/admin/scraper-self-test`, {
        params: { url: url || undefined },
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Self-test failed to run');
    } finally {
      setRunning(false);
    }
  };

  const status = result ? (STATUS_STYLES[result.status] || STATUS_STYLES.error) : null;
  const StatusIcon = status?.icon;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 mb-8" data-testid="scraper-self-test-panel">
      <div className="flex items-center gap-2 mb-1">
        <Stethoscope className="h-4 w-4 text-violet-500" />
        <h2 className="text-sm font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Scraper self-test</h2>
      </div>
      <p className="text-xs text-gray-500 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
        Runs a real listing through the Apify scraper and shows which fields came back. Use this to instantly spot if a scraper breaks (e.g. an actor changes its data format). Leave blank to test the default Zillow listing.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Optional: paste a Zillow / Realtor / Redfin listing URL…"
          className="flex-1"
          data-testid="self-test-url-input"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        />
        <Button onClick={run} disabled={running} data-testid="run-self-test-btn" className="whitespace-nowrap">
          {running ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Running…</> : 'Run self-test'}
        </Button>
      </div>

      {running && (
        <p className="text-xs text-gray-400 mt-3">This can take up to 60–90 seconds while the scraper fetches live data…</p>
      )}

      {result && (
        <div className="mt-5" data-testid="self-test-result">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {StatusIcon && <StatusIcon className={`h-5 w-5 ${status.cls}`} />}
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${status.badge}`} data-testid="self-test-status">
              {status.label}
            </span>
            <span className="text-xs text-gray-500">{result.message}</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-gray-400 uppercase tracking-wide text-[10px]">Method</div>
              <div className="font-medium text-black mt-0.5">{result.extraction_method || '—'}</div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-gray-400 uppercase tracking-wide text-[10px]">Photos</div>
              <div className="font-medium text-black mt-0.5" data-testid="self-test-photo-count">{result.photo_count ?? 0}</div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-gray-400 uppercase tracking-wide text-[10px]">Real photos</div>
              <div className="font-medium text-black mt-0.5">{result.has_real_photos ? 'Yes' : 'No'}</div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-gray-400 uppercase tracking-wide text-[10px]">Apify token</div>
              <div className="font-medium text-black mt-0.5">{result.apify_token_configured ? 'Configured' : 'Missing'}</div>
            </div>
          </div>

          {result.fields && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(result.fields).map(([field, info]) => (
                <div key={field} className="flex items-center gap-2 text-xs p-2 rounded-lg border border-gray-100" data-testid={`self-test-field-${field}`}>
                  {info.populated
                    ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    : <XCircle className="h-3.5 w-3.5 text-red-400 shrink-0" />}
                  <span className="font-medium text-gray-700 capitalize">{field.replace(/_/g, ' ')}</span>
                  <span className="text-gray-400 truncate ml-auto">{info.populated ? info.value : 'empty'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const AdminUsersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (user && !isOwner(user)) navigate('/dashboard');
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/admin/users/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(data);
    } catch (e) {
      // ignore
    }
  };

  const fetchUsers = async (query = '') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/admin/users`, {
        params: { q: query || undefined, limit: 200 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchUsers('');
  }, []);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => fetchUsers(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const handleSaved = (updated, deletedId) => {
    if (deletedId) {
      setUsers(prev => prev.filter(u => u.user_id !== deletedId));
    } else if (updated) {
      setUsers(prev => prev.map(u => (u.user_id === updated.user_id ? { ...u, ...updated } : u)));
    }
    setSelected(null);
    fetchStats();
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admin-users-page">
      <AppNavbar />

      <main className="max-w-screen-xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="users-page-title">
              Founder dashboard
            </h1>
            <p className="text-sm text-gray-500 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Manage every user, plan, and role across RealProp.
            </p>
          </div>
          <Link to="/design-studio" className="text-sm text-gray-500 hover:text-black" data-testid="link-to-admin-templates">
            → Template Design Studio
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users} label="Total users" value={stats?.total_users ?? '—'} />
          <StatCard icon={DollarSign} label="Paid users" value={stats?.paid_users ?? '—'} accent="text-emerald-500" />
          <StatCard icon={TrendingUp} label="New (30d)" value={stats?.new_signups_30d ?? '—'} accent="text-violet-500" />
          <StatCard icon={DollarSign} label="MRR (est.)" value={stats ? `$${stats.mrr_estimate_usd}` : '—'} accent="text-emerald-500" />
        </div>

        <ScraperSelfTest />

        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex items-center gap-3">
            <Search className="h-4 w-4 text-gray-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email or name…"
              className="border-none focus-visible:ring-0 px-0"
              data-testid="users-search-input"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            />
            <span className="text-xs text-gray-400 whitespace-nowrap">{users.length} shown</span>
          </div>

          {loading ? (
            <div className="p-12 flex items-center justify-center text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading users…
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center text-gray-500 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
              No users match.
            </div>
          ) : (
            <table className="w-full text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">User</th>
                  <th className="px-4 py-3 text-left font-medium">Plan</th>
                  <th className="px-4 py-3 text-left font-medium">Role</th>
                  <th className="px-4 py-3 text-left font-medium">Joined</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map(u => (
                  <tr key={u.user_id} className={`hover:bg-gray-50 cursor-pointer ${u.suspended ? 'opacity-60' : ''}`} onClick={() => setSelected(u)} data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-black flex items-center gap-2">
                        {u.name}
                        {u.suspended && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700"><Ban className="h-2.5 w-2.5" />Suspended</span>}
                      </div>
                      <div className="text-xs text-gray-500">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${PLAN_COLORS[u.plan_type] || PLAN_COLORS.starter}`}>
                        {u.plan_type || 'starter'}
                      </span>
                    </td>
                    <td className="px-4 py-3"><RoleBadge role={u.role || 'user'} /></td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button className="text-xs text-violet-600 hover:underline" data-testid={`edit-user-${u.email}`}>
                        Manage →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {selected && <UserDrawer targetUser={selected} onClose={() => setSelected(null)} onSaved={handleSaved} />}
    </div>
  );
};

export default AdminUsersPage;
